import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default SEO values
const DEFAULT_SEO = {
  siteName: 'Caltavuturo Climbing',
  siteUrl: 'https://caltavuturoclimbing.it',
  defaultImage: 'https://customer-assets.emergentagent.com/job_sport-climb-cms/artifacts/t2zfhsiq_herosection.jpg',
  twitterHandle: '@caltavuturoclimbing',
};

const PAGE_SEO_DEFAULTS = {
  home: {
    meta_title: {
      it: 'Caltavuturo Climbing | Arrampicata Sportiva in Sicilia',
      en: 'Caltavuturo Climbing | Sport Climbing in Sicily',
      fr: 'Caltavuturo Climbing | Escalade Sportive en Sicile',
      de: 'Caltavuturo Climbing | Sportklettern auf Sizilien',
      es: 'Caltavuturo Climbing | Escalada Deportiva en Sicilia',
    },
    meta_description: {
      it: 'Scopri le falesie di Caltavuturo, nel cuore delle Madonie in Sicilia. 7 settori, oltre 100 vie di arrampicata sportiva dal 4a all\'8a.',
      en: 'Discover the crags of Caltavuturo, in the heart of the Madonie mountains in Sicily. 7 sectors, over 100 sport climbing routes from 4a to 8a.',
      fr: 'Découvrez les falaises de Caltavuturo, au cœur des Madonie en Sicile. 7 secteurs, plus de 100 voies d\'escalade sportive de 4a à 8a.',
      de: 'Entdecken Sie die Klettergärten von Caltavuturo im Herzen der Madonie auf Sizilien. 7 Sektoren, über 100 Sportkletterrouten von 4a bis 8a.',
      es: 'Descubre los sectores de Caltavuturo, en el corazón de las Madonie en Sicilia. 7 sectores, más de 100 vías de escalada deportiva de 4a a 8a.',
    },
    meta_keywords: {
      it: 'arrampicata sicilia, falesie caltavuturo, climbing sicily, sport climbing madonie, escalada sicilia',
      en: 'climbing sicily, caltavuturo crags, sport climbing madonie, sicily bouldering, mediterranean climbing',
      fr: 'escalade sicile, falaises caltavuturo, escalade sportive madonie, grimpe sicile',
      de: 'klettern sizilien, klettergärten caltavuturo, sportklettern madonie, sizilien klettern',
      es: 'escalada sicilia, sectores caltavuturo, escalada deportiva madonie, climbing sicilia',
    }
  },
  about: {
    meta_title: {
      it: 'Chi Siamo | Caltavuturo Climbing',
      en: 'About Us | Caltavuturo Climbing',
      fr: 'Qui sommes-nous | Caltavuturo Climbing',
      de: 'Über uns | Caltavuturo Climbing',
      es: 'Quiénes somos | Caltavuturo Climbing',
    },
    meta_description: {
      it: 'L\'associazione Caltavuturo Climbing promuove l\'arrampicata sportiva in Sicilia dal 1990. Scopri la nostra storia e il nostro team.',
      en: 'Caltavuturo Climbing association has been promoting sport climbing in Sicily since 1990. Discover our history and team.',
      fr: 'L\'association Caltavuturo Climbing promeut l\'escalade sportive en Sicile depuis 1990.',
      de: 'Der Verein Caltavuturo Climbing fördert das Sportklettern auf Sizilien seit 1990.',
      es: 'La asociación Caltavuturo Climbing promueve la escalada deportiva en Sicilia desde 1990.',
    }
  },
  crags: {
    meta_title: {
      it: 'Falesie | Caltavuturo Climbing',
      en: 'Crags | Caltavuturo Climbing',
      fr: 'Falaises | Caltavuturo Climbing',
      de: 'Klettergärten | Caltavuturo Climbing',
      es: 'Sectores | Caltavuturo Climbing',
    },
    meta_description: {
      it: 'Esplora le 7 falesie di Caltavuturo: Gazzara, Cabeci, Rocca Piccola, Rocca di Sciara e altre. Vie dal 4a all\'8a su calcare.',
      en: 'Explore the 7 crags of Caltavuturo: Gazzara, Cabeci, Rocca Piccola, Rocca di Sciara and more. Routes from 4a to 8a on limestone.',
      fr: 'Explorez les 7 falaises de Caltavuturo: Gazzara, Cabeci, Rocca Piccola, Rocca di Sciara et plus.',
      de: 'Erkunden Sie die 7 Klettergärten von Caltavuturo: Gazzara, Cabeci, Rocca Piccola, Rocca di Sciara und mehr.',
      es: 'Explora los 7 sectores de Caltavuturo: Gazzara, Cabeci, Rocca Piccola, Rocca di Sciara y más.',
    }
  },
  contact: {
    meta_title: {
      it: 'Contatti | Caltavuturo Climbing',
      en: 'Contact | Caltavuturo Climbing',
      fr: 'Contact | Caltavuturo Climbing',
      de: 'Kontakt | Caltavuturo Climbing',
      es: 'Contacto | Caltavuturo Climbing',
    },
    meta_description: {
      it: 'Contatta l\'associazione Caltavuturo Climbing. Via Dante Alighieri 23, Caltavuturo (PA). Tel: +39 379 1243094',
      en: 'Contact Caltavuturo Climbing association. Via Dante Alighieri 23, Caltavuturo (PA). Tel: +39 379 1243094',
      fr: 'Contactez l\'association Caltavuturo Climbing. Via Dante Alighieri 23, Caltavuturo (PA).',
      de: 'Kontaktieren Sie den Verein Caltavuturo Climbing. Via Dante Alighieri 23, Caltavuturo (PA).',
      es: 'Contacta con la asociación Caltavuturo Climbing. Via Dante Alighieri 23, Caltavuturo (PA).',
    }
  },
  donations: {
    meta_title: {
      it: 'Donazioni | Supporta Caltavuturo Climbing',
      en: 'Donations | Support Caltavuturo Climbing',
      fr: 'Dons | Soutenez Caltavuturo Climbing',
      de: 'Spenden | Unterstützen Sie Caltavuturo Climbing',
      es: 'Donaciones | Apoya Caltavuturo Climbing',
    },
    meta_description: {
      it: 'Supporta lo sviluppo delle falesie di Caltavuturo con una donazione. Aiutaci a creare nuove vie e mantenere quelle esistenti.',
      en: 'Support the development of Caltavuturo crags with a donation. Help us create new routes and maintain existing ones.',
      fr: 'Soutenez le développement des falaises de Caltavuturo avec un don.',
      de: 'Unterstützen Sie die Entwicklung der Klettergärten von Caltavuturo mit einer Spende.',
      es: 'Apoya el desarrollo de los sectores de Caltavuturo con una donación.',
    }
  }
};

export function SEOHead({ pageKey, customData = {} }) {
  const { language } = useLanguage();
  const [seoData, setSeoData] = useState(null);

  useEffect(() => {
    const fetchSEO = async () => {
      try {
        const res = await axios.get(`${API}/seo/${pageKey}`);
        setSeoData(res.data);
      } catch (error) {
        // Use defaults if fetch fails
        setSeoData(null);
      }
    };
    if (pageKey) {
      fetchSEO();
    }
  }, [pageKey]);

  // Get the default data for this page
  const defaultData = PAGE_SEO_DEFAULTS[pageKey] || PAGE_SEO_DEFAULTS.home;
  
  // Helper to safely get translated string from an object - always returns a string
  const getTranslatedString = (obj, fallback = '') => {
    if (obj === null || obj === undefined) return String(fallback);
    if (typeof obj === 'string') return obj.length > 0 ? obj : String(fallback);
    if (typeof obj === 'object' && obj !== null) {
      const value = obj[language] || obj['it'] || obj['en'] || '';
      return typeof value === 'string' && value.length > 0 ? value : String(fallback);
    }
    return String(fallback);
  };

  // Get title - prioritize customData, then seoData from API, then defaults
  const rawTitle = customData?.meta_title || seoData?.meta_title || defaultData?.meta_title;
  const title = getTranslatedString(rawTitle, DEFAULT_SEO.siteName);

  // Get description
  const rawDescription = customData?.meta_description || seoData?.meta_description || defaultData?.meta_description;
  const description = getTranslatedString(rawDescription, '');

  // Get keywords
  const rawKeywords = customData?.meta_keywords || seoData?.meta_keywords || defaultData?.meta_keywords;
  const keywords = getTranslatedString(rawKeywords, '');

  const image = customData?.og_image || seoData?.og_image || DEFAULT_SEO.defaultImage;
  const canonical = customData?.canonical_url || seoData?.canonical_url || '';
  const robots = customData?.robots || seoData?.robots || 'index, follow';

  // Final safety check - ensure title is ALWAYS a valid non-empty string
  const safeTitle = (typeof title === 'string' && title.trim().length > 0) 
    ? title 
    : DEFAULT_SEO.siteName;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={language} />
      <title>{safeTitle}</title>
      <meta name="description" content={description || ''} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robots} />
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={safeTitle} />
      <meta property="og:description" content={description || ''} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={DEFAULT_SEO.siteName} />
      <meta property="og:locale" content={language === 'it' ? 'it_IT' : language === 'en' ? 'en_US' : `${language}_${language.toUpperCase()}`} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={safeTitle} />
      <meta name="twitter:description" content={description || ''} />
      <meta name="twitter:image" content={image} />

      {/* Additional SEO */}
      <meta name="geo.region" content="IT-PA" />
      <meta name="geo.placename" content="Caltavuturo" />
      <meta name="geo.position" content="37.8221;13.8919" />
      <meta name="ICBM" content="37.8221, 13.8919" />
    </Helmet>
  );
}

// SEO for dynamic crag pages
export function CragSEOHead({ crag }) {
  const { language } = useLanguage();
  
  // Return null if no crag data
  if (!crag || !crag.name) {
    return null;
  }

  // Build title as a plain string - no template literals that could cause issues
  const cragName = String(crag.name || 'Falesia');
  const pageTitle = cragName + ' | Caltavuturo Climbing';
  
  // Build description as a plain string
  let pageDescription = '';
  if (crag.description) {
    if (typeof crag.description === 'string') {
      pageDescription = crag.description;
    } else if (typeof crag.description === 'object') {
      pageDescription = crag.description[language] || crag.description['it'] || crag.description['en'] || '';
    }
  }
  if (!pageDescription) {
    pageDescription = cragName + ' - arrampicata sportiva a Caltavuturo, Sicilia';
  }
  
  const pageImage = (crag.images && crag.images[0]) ? crag.images[0] : DEFAULT_SEO.defaultImage;
  const pageKeywords = cragName + ', arrampicata sicilia, climbing sicily, falesie caltavuturo';

  return (
    <Helmet>
      <html lang={language} />
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />
    </Helmet>
  );
}
